<template>
  <div ref="infoBoxRef" class="info-box-content" v-show="false">
    <slot></slot>
  </div>
</template>

<script lang="ts" setup>
import { inject, ref, watch, onUnmounted } from "vue"
import { GCJ02toBD09, BD09toGCJ02, WGS84toGCJ02 } from "@p-v/utils/amap"

interface ComponentProps {
  point: { lng: number; lat: number }
  offset?: { width: number; height: number }
  size?: { width: number; height: number }
  gpsType?: "GCJ02" | "WGS84" | "BD09"
  title?: string
  opacity?: number
  autoMove?: boolean
  data?: any
}

const props = withDefaults(defineProps<ComponentProps>(), {
  point: () => ({
    lng: 116.397428,
    lat: 39.90923,
  }),
  offset: () => ({ width: 0, height: 0 }),
  size: () => ({ width: 300, height: 150 }),
  gpsType: () => "GCJ02",
  title: () => "",
  opacity: () => 1,
  autoMove: () => true,
  data: () => ({}),
})

const amap = inject("amap") as any
const infoBoxRef = ref<HTMLElement | null>(null)
const infoWindow = ref<any>(null)

// 更新信息窗口
const updateInfoWindow = () => {
  if (!amap.value) return

  // 处理不同坐标系
  let position = props.point

  if (props.gpsType === "BD09") {
    position = BD09toGCJ02(props.point.lng, props.point.lat)
  } else if (props.gpsType === "WGS84") {
    position = WGS84toGCJ02(props.point.lng, props.point.lat)
  }

  // 创建信息窗口
  if (infoWindow.value) {
    amap.value.remove(infoWindow.value)
  }

  if (infoBoxRef.value) {
    infoWindow.value = new window.AMap.InfoWindow({
      content: infoBoxRef.value,
      offset: new window.AMap.Pixel(props.offset.width, props.offset.height),
      size: new window.AMap.Size(props.size.width, props.size.height),
      position: [position.lng, position.lat],
      autoMove: props.autoMove,
      isCustom: true,
    })

    // 显示信息窗口
    infoWindow.value.open(amap.value, [position.lng, position.lat])
  }
}

// 监听地图和DOM加载状态
watch(
  () => [amap.value, infoBoxRef.value],
  ([mapInstance, domRef]) => {
    if (mapInstance && domRef) {
      updateInfoWindow()
    }
  },
  { immediate: true },
)

// 监听位置变化
watch(
  () => props.point,
  () => {
    if (infoWindow.value && amap.value) {
      let position = props.point
      if (props.gpsType === "BD09") {
        position = BD09toGCJ02(props.point.lng, props.point.lat)
      } else if (props.gpsType === "WGS84") {
        position = WGS84toGCJ02(props.point.lng, props.point.lat)
      }
      infoWindow.value.setPosition([position.lng, position.lat])
    }
  },
  { deep: true },
)

// 组件销毁时移除信息窗口
onUnmounted(() => {
  if (infoWindow.value && amap.value) {
    infoWindow.value.close()
    amap.value.remove(infoWindow.value)
    infoWindow.value = null
  }
})

// 暴露信息窗口对象
defineExpose({
  infoWindow,
})
</script>

<style>
.info-box-content {
  display: block;
}
</style>
